import { useState, useMemo, useCallback, useEffect } from 'react';
import { payslipApi, MONTH_OPTIONS, YEAR_OPTIONS } from '../api/payslipApi';
import { appToast } from '../../../../components/AppToast';

export function usePayslipList() {
  const [monthFilter, setMonthFilter] = useState('Apr');
  const [yearFilter, setYearFilter] = useState('2026');
  const [searchField, setSearchField] = useState('Pay Head');
  const [searchText, setSearchText] = useState('');
  const [payHeadTypeFilter, setPayHeadTypeFilter] = useState('All');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedSlipDetail, setSelectedSlipDetail] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // User identity details for payslip printout
  const employeeInfo = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        name: stored.name || stored.userName || 'Divyanshu Singh',
        empId: stored.employee_id || stored.empCode || '348414',
        code: stored.code || 'EMP-102',
        department: stored.department || 'Product Strategy',
        designation: stored.role || stored.designation || 'Software Engineer',
        pan: 'ABCDE1234F',
        bankAccount: '•••• •••• •••• 9102',
        bankName: 'HDFC Bank Ltd.',
        pfNo: 'MH/BAN/0045129/000/0102',
        location: 'Corporate HQ, Mumbai',
      };
    } catch {
      return {
        name: 'Divyanshu Singh',
        empId: '348414',
        code: 'EMP-102',
        department: 'Product Strategy',
        designation: 'Software Engineer',
        pan: 'ABCDE1234F',
        bankAccount: '•••• •••• •••• 9102',
        bankName: 'HDFC Bank Ltd.',
        pfNo: 'MH/BAN/0045129/000/0102',
        location: 'Corporate HQ, Mumbai',
      };
    }
  }, []);

  const searchFieldOptions = [
    { value: 'Pay Head', label: 'Pay Head' },
    { value: 'Remarks', label: 'Remarks' },
  ];

  const payHeadTypeOptions = [
    { value: 'All', label: 'All Types' },
    { value: 'Earnings', label: 'Earnings (Cr)' },
    { value: 'Deductions', label: 'Deductions (Dr)' },
  ];

  const tools = [
    { key: 'view-slip', label: 'View Printable Pay Slip', icon: 'visibility' },
    { key: 'pdf', label: 'Export Pay Slip to PDF', icon: 'picture_as_pdf' },
    { key: 'excel', label: 'Export Breakdown to Excel', icon: 'table_view' },
    { key: 'print', label: 'Print Current View', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await payslipApi.getList();
      setRecords(res.data || []);
    } catch {
      // Fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Month filter
      if (monthFilter !== 'All' && item.month !== monthFilter) {
        return false;
      }

      // Year filter
      if (yearFilter !== 'All' && item.year !== yearFilter) {
        return false;
      }

      // Pay head type
      if (payHeadTypeFilter !== 'All' && item.payHeadType !== payHeadTypeFilter) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Pay Head' && !item.payHead?.toLowerCase().includes(query)) return false;
        if (searchField === 'Remarks' && !item.remarks?.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, monthFilter, yearFilter, payHeadTypeFilter, searchText, searchField]);

  // Summary computations
  const summary = useMemo(() => {
    let grossEarnings = 0;
    let totalDeductions = 0;
    let earningsCount = 0;
    let deductionsCount = 0;

    filteredRecords.forEach((r) => {
      if (r.drCr === 'Cr') {
        grossEarnings += Number(r.amount || 0);
        earningsCount++;
      } else if (r.drCr === 'Dr') {
        totalDeductions += Number(r.amount || 0);
        deductionsCount++;
      }
    });

    const netSalary = grossEarnings - totalDeductions;

    return {
      grossEarnings,
      totalDeductions,
      netSalary,
      totalPayheads: filteredRecords.length,
      earningsCount,
      deductionsCount,
    };
  }, [filteredRecords]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Pay slip records filtered');
  }, []);

  const resetFilters = useCallback(() => {
    setMonthFilter('Apr');
    setYearFilter('2026');
    setPayHeadTypeFilter('All');
    setSearchText('');
    setSearchField('Pay Head');
    setPage(1);
  }, []);

  const handleOpenSlipModal = useCallback(() => {
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseSlipModal = useCallback(() => {
    setIsDetailModalOpen(false);
  }, []);

  const handleToolSelect = useCallback(
    (toolKey) => {
      const key = String(toolKey || '').toLowerCase();
      if (key.includes('view-slip')) {
        setIsDetailModalOpen(true);
      } else if (key.includes('print')) {
        window.print();
      } else if (key.includes('pdf')) {
        appToast.success(`Generating PDF Pay Slip for ${monthFilter} ${yearFilter}...`);
      } else if (key.includes('excel')) {
        appToast.success('Exporting Pay Slip breakdown to Excel...');
      }
    },
    [monthFilter, yearFilter]
  );

  return {
    state: {
      records: paginatedRecords,
      allFilteredRecords: filteredRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      monthFilter,
      yearFilter,
      payHeadTypeFilter,
      searchField,
      searchText,
      isLoading,
      monthOptions: MONTH_OPTIONS,
      yearOptions: YEAR_OPTIONS,
      payHeadTypeOptions,
      searchFieldOptions,
      tools,
      summary,
      employeeInfo,
      isDetailModalOpen,
      selectedSlipDetail,
    },
    actions: {
      setMonthFilter,
      setYearFilter,
      setPayHeadTypeFilter,
      setSearchField,
      setSearchText,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleToolSelect,
      handleOpenSlipModal,
      handleCloseSlipModal,
    },
  };
}
